import { ReactNode } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import { Box, Card, Stack, Typography } from "@mui/material";

import Logo from "../../components/logo";

type Props = {
  children: ReactNode;
  heroTitle: string;
  heroDescription: string;
  heroImageSrc?: string;
  heroImageAlt?: string;
  topContent?: ReactNode;
  footer?: ReactNode;
};

export default function AuthOnboardingLayout({
  children,
  heroTitle,
  heroDescription,
  heroImageSrc = "/assets/images/auth/alfred-cadastro.png",
  heroImageAlt = "Alfred",
  topContent,
  footer,
}: Props) {
  const theme = useTheme();

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(360px, 0.9fr) minmax(520px, 1.1fr)",
        },
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#090A0C",
          borderRight: `1px solid ${alpha(theme.palette.grey[500], 0.18)}`,
        }}
      >
        <Logo sx={{ position: "absolute", top: 36, left: 40, zIndex: 2 }} />

        <Box
          component="img"
          src={heroImageSrc}
          alt={heroImageAlt}
          sx={{
            width: "auto",
            maxWidth: { md: "64%", lg: "58%" },
            height: "auto",
            maxHeight: "68vh",
            objectFit: "contain",
            objectPosition: "center center",
            opacity: 0.96,
          }}
        />

        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(9,10,12,0.08) 40%, rgba(9,10,12,0.96) 100%)",
          }}
        />

        <Stack
          spacing={1}
          sx={{
            position: "absolute",
            left: 48,
            right: 48,
            bottom: 44,
            color: "common.white",
          }}
        >
          <Typography variant="h3" maxWidth={520}>
            {heroTitle}
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: alpha(theme.palette.common.white, 0.72) }}
            maxWidth={500}
          >
            {heroDescription}
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: { xs: 2, sm: 4, lg: 8 },
          py: { xs: 3, sm: 6 },
        }}
      >
        <Stack spacing={3} sx={{ width: 1, maxWidth: 540 }}>
          <Logo sx={{ display: { xs: "block", md: "none" }, mb: 2 }} />

          {topContent}

          <Card
            variant="outlined"
            sx={{
              p: { xs: 2.5, sm: 4 },
              borderColor: "divider",
              bgcolor: alpha(
                theme.palette.background.paper,
                theme.palette.mode === "light" ? 0.92 : 0.72
              ),
              backdropFilter: "blur(12px)",
              boxShadow: theme.customShadows?.z8,
            }}
          >
            {children}
          </Card>

          {footer && (
            <Typography
              component="div"
              variant="caption"
              color="text.disabled"
              textAlign="center"
            >
              {footer}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
}
